import { type FC, useCallback, useEffect, useState } from "react";

import type { DashboardFlowDetailResponse } from "../../../dashboard-client/types.gen";
import type { TaskSnapshot } from "../../../types";

import { getFlowDetailsAsUser } from "../../../dashboard-client";
import { useDashboardRealtime } from "../../../hooks/useDashboardRealtime";
import { Alert } from "../../common/Alert";
import { Badge } from "../../common/Badge";
import { LoadingState } from "../../common/LoadingState";
import { StatusBadge } from "../../common/StatusBadge";
import {
  FlowTasksList,
  stripFlowPrefix,
} from "../../dashboard/FlowDetailModal";
import { StageJournalPanel } from "../../dashboard/StageJournalPanel";
import { ExecutionQaMandateCard } from "../ExecutionQaMandateCard";
import {
  CompileRecordingPropertyCard,
  CompileRecordingTraceList,
  formatFlowStatusLabel,
} from "./CompileRecordingComponents";
import { CompileRecordingReplayPanel } from "./CompileRecordingReplayPanel";

type CompileRecordingFlowDetailResponse = Extract<
  DashboardFlowDetailResponse,
  { flow_type: "compile_recording" }
>;

function assertCompileRecordingFlowDetail(
  detail: DashboardFlowDetailResponse,
): CompileRecordingFlowDetailResponse {
  if (detail.flow_type !== "compile_recording") {
    throw new Error(
      `Expected compile_recording flow detail, got '${detail.flow_type}'.`,
    );
  }
  return detail;
}

export const CompileRecordingBody: FC<{
  flowRunId: string;
  tasks: TaskSnapshot[];
}> = ({ flowRunId, tasks }) => {
  const [detail, setDetail] =
    useState<CompileRecordingFlowDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { subscribe } = useDashboardRealtime();

  const rawId = stripFlowPrefix(flowRunId);

  const fetchFlowDetail = useCallback(async () => {
    const { data } = await getFlowDetailsAsUser({ path: { flowId: rawId } });
    if (!data) {
      throw new Error("Compile recording flow detail response is empty.");
    }
    return assertCompileRecordingFlowDetail(data);
  }, [rawId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    fetchFlowDetail()
      .then((data) => {
        if (!cancelled) {
          setDetail(data);
        }
      })
      .catch((loadError: unknown) => {
        if (!cancelled) {
          setError(
            loadError instanceof Error
              ? loadError.message
              : "Failed to load compile recording flow.",
          );
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [fetchFlowDetail]);

  useEffect(() => {
    let timeout: number | null = null;
    const scheduleRefresh = () => {
      if (timeout !== null) {
        return;
      }
      timeout = window.setTimeout(() => {
        timeout = null;
        fetchFlowDetail()
          .then(setDetail)
          .catch((refreshError: unknown) => {
            console.error("Background refresh failed:", refreshError);
          });
      }, 500);
    };

    const unsubscribe = subscribe(
      (message) => {
        if (message.type !== "compile_recording_snapshot") {
          return;
        }
        const compileRecordingRequestId =
          typeof message.payload.compile_recording_request_id === "string"
            ? message.payload.compile_recording_request_id
            : null;
        if (compileRecordingRequestId !== rawId) {
          return;
        }
        scheduleRefresh();
      },
      { topic: "internalEventTopic" },
    );

    return () => {
      unsubscribe();
      if (timeout !== null) {
        window.clearTimeout(timeout);
      }
    };
  }, [fetchFlowDetail, rawId, subscribe]);

  if (loading) {
    return <LoadingState message="Loading compile recording flow..." />;
  }
  if (error) {
    return <Alert variant="error">{error}</Alert>;
  }
  if (!detail) {
    return (
      <Alert variant="warning">
        Compile recording flow details are unavailable.
      </Alert>
    );
  }

  const recording = detail.compile_recording;
  const summary =
    recording.summary ?? "Compile before record flow is in progress.";
  const { task } = detail;

  return (
    <div className="space-y-6">
      <div className="overflow-hidden rounded-none border border-blue-200 bg-gradient-to-br from-blue-50 via-white to-orange-50 shadow-[6px_6px_0_rgba(37,99,235,0.12)] dark:border-blue-500/20 dark:from-slate-950 dark:via-slate-950 dark:to-orange-950/20 dark:shadow-[6px_6px_0_rgba(15,23,42,0.3)]">
        <div className="border-b border-blue-200/80 px-5 py-4 dark:border-white/10">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="space-y-2">
              <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-blue-700 dark:text-blue-300">
                Compile Before Record
              </div>
              <h3 className="font-mono text-xl font-semibold text-slate-950 dark:text-slate-50">
                {summary}
              </h3>
              <p className="max-w-3xl text-sm text-slate-600 dark:text-slate-300">
                This continuation flow compiles the workspace, brings localhost
                online, and records the QA mandate against the live runtime.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <StatusBadge
                status={recording.status}
                label={formatFlowStatusLabel(recording.status)}
              />
              {detail.execution_flow?.branch_name ? (
                <Badge variant="info" size="sm">
                  {detail.execution_flow.branch_name}
                </Badge>
              ) : null}
              {recording.recording_artifact_url ? (
                <Badge variant="success" size="sm">
                  Replay Ready
                </Badge>
              ) : null}
            </div>
          </div>
        </div>

        <CompileRecordingReplayPanel
          artifactPath={recording.recording_artifact_path}
          artifactUrl={recording.recording_artifact_url}
          branchName={detail.execution_flow?.branch_name ?? null}
          recordingSessionId={recording.recording_session_id}
          targetBaseUrl={recording.target_base_url}
        />

        <div className="grid grid-cols-2 gap-3 p-4 sm:grid-cols-3">
          <CompileRecordingPropertyCard
            label="Target Base URL"
            value={recording.target_base_url}
          />
          <CompileRecordingPropertyCard
            label="Workspace Compose"
            value={recording.docker_compose_path}
          />
          <CompileRecordingPropertyCard
            label="Recording Session"
            value={recording.recording_session_id}
          />
          <CompileRecordingPropertyCard
            label="Artifact Path"
            value={recording.recording_artifact_path}
          />
          <CompileRecordingPropertyCard
            label="Execution Flow"
            value={recording.execution_request_id}
          />
          {task?.assigned_node ? (
            <CompileRecordingPropertyCard
              label="Assigned Worker"
              value={task.assigned_node}
            />
          ) : null}
        </div>
      </div>

      {recording.qa_mandate ? (
        <ExecutionQaMandateCard qaMandate={recording.qa_mandate} />
      ) : null}

      <CompileRecordingTraceList logs={detail.logs} />

      <StageJournalPanel flowId={rawId} flowType="compile_recording" />

      <FlowTasksList tasks={tasks} />
    </div>
  );
};
